import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import capitalize from 'lodash/capitalize';
import map from 'lodash/map';
import moment from 'moment';
import { Tooltip } from '@material-ui/core';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import { SPACES } from 'common/constants/sizes';
import { SHORT_DATE_FORMAT, TRACKING } from 'common/constants/constants';
import { pluralize, roundValue } from 'common/utils/utils';
import { PROJECTS } from './projects.services';
import { DELIVERY_STATES_KEYS } from '../projects.consts';
import styled from 'styled-components';
import { getColor } from 'common/constants/_colors';
import * as TrackingService from 'common/services/tracking.service';
import { getPmViewerFromStore } from 'components/hooks/PM/pm-provider-viewers';
import { upperCase } from 'lodash';

const LinkifiedText = styled.span`
  color: ${getColor('chambray')};
  cursor: pointer;
  padding: ${SPACES.small};
`;

export const iterationTitle = (iteration, isIterationInBold) => {
  if (isEmpty(iteration)) return '';
  const { name, start, end } = iteration;
  const dates = `${moment(start).format(SHORT_DATE_FORMAT)} - ${moment(
    end
  ).format(SHORT_DATE_FORMAT)}`;

  return isIterationInBold ? (
    <Flex>
      <Text size='medium' weight='500' pr='0.4rem'>
        {name} -
      </Text>
      <Text size='medium'>{dates}</Text>
    </Flex>
  ) : (
    `${name} - ${dates}`
  );
};

export const getIssuesTableColumns = ({
  setSelectedDeliveryState,
  deliveryState,
  numOfIssues = 0,
  numOfStorypoints = 0,
  isSummary,
  isPulse = false
}) => {
  const pmViewer = getPmViewerFromStore();
  const { issuesTableTitle } = getDeliveryTextsByKey(deliveryState);
  return [
    ...(isSummary
      ? [
          {
            dataPoint: 'summary',
            name: (
              <Text size='medium'>
                {numOfIssues} {capitalize(issuesTableTitle)}
                {isSummary && numOfIssues > 3 && (
                  <LinkifiedText
                    onClick={() => {
                      setSelectedDeliveryState(deliveryState);
                      document.querySelector(
                        '#iterations_scroll'
                      ).scrollTop = 0;
                      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                        page: isPulse
                          ? TRACKING.PAGES.PULSE
                          : TRACKING.PAGES.PROJECTS,
                        click_type:
                          TRACKING.CLICK.PDT_ISSUES_TABLE_DELIVERY_TYPE_SELECT,
                        selected_delivery_type: deliveryState
                      });
                    }}
                  >
                    More &#62;
                  </LinkifiedText>
                )}
              </Text>
            )
          }
        ]
      : [
          {
            isSortedCol: true,
            name: 'TYPE',
            dataPoint: 'issue_type'
          },
          {
            isSortedCol: true,
            name: 'NAME',
            dataPoint: 'summary',
            tableCellProps: {
              style: {
                borderLeft: 0
              }
            }
          }
        ]),
    {
      isSortedCol: !isSummary,
      name: 'ASSIGNEE',
      dataPoint: 'assignee'
    },
    {
      isSortedCol: !isSummary,
      dataPoint: 'story_points',
      name: `${upperCase(pmViewer.storyPoint.plural)} (${roundValue(
        numOfStorypoints
      )})`
    }
  ];
};

export const getIssuesTableData = (issuesGroupedByDeliveryState, isSummary) => {
  const tableData = map(
    issuesGroupedByDeliveryState,
    ({
      provider_type_icon_url,
      summary,
      assignee,
      story_points,
      issue_provider_url,
      issue_type
    }) => {
      const isIterationSummaryOpen =
        window.location.search.includes('iteration_summary');
      const tooltipPlacement = isIterationSummaryOpen ? 'left' : 'bottom';
      return {
        issue_provider_url,
        summary: isSummary ? (
          <Flex items='center'>
            <Tooltip title={issue_type} placement={tooltipPlacement}>
              <Flex width='2rem'>
                <img className='size-4' src={provider_type_icon_url} alt='' />
              </Flex>
            </Tooltip>
            <Tooltip title={summary} placement={tooltipPlacement}>
              <Text size='medium' width='270px' ellipsisAfter='270px'>
                {summary}
              </Text>
            </Tooltip>
          </Flex>
        ) : (
          <Tooltip title={summary}>
            <Text size='medium' width='220px' ellipsisAfter='220px'>
              {summary}
            </Text>
          </Tooltip>
        ),

        assignee: (
          <Tooltip title={assignee}>
            <Text size='medium' width='90px' ellipsisAfter='90px'>
              {assignee}
            </Text>
          </Tooltip>
        ),
        story_points: (
          <Text
            width='100px'
            ellipsisAfter='100px'
            color={PROJECTS.SECONDARY_COLOR}
          >
            {pluralize(roundValue(story_points), 'point')}
          </Text>
        ),
        ...(isSummary
          ? {}
          : {
              issue_type: (
                <Flex width='2.5rem' display='flex' justify='center'>
                  <img src={provider_type_icon_url} className='size-4' alt='' />
                </Flex>
              )
            })
      };
    }
  );

  return tableData;
};

export const fetchGraphLabels = ({ iterations, countsKey, storyPointsKey }) => {
  let labelsObj = {};
  iterations.forEach(
    ({ [countsKey]: counts, [storyPointsKey]: storyPoints }) => {
      labelsObj = { ...labelsObj, ...counts, ...storyPoints };
    }
  );
  return Object.keys(labelsObj);
};

export const getChartsData = ({
  project,
  totalCountsKey,
  totalStoryPointsKey,
  countsKey,
  storyPointsKey,
  calculationKey,
  isScrumProject,
  forceTotal
}) => {
  const {
    iterations = [],
    [totalCountsKey]: totalCounts,
    [totalStoryPointsKey]: totalStoryPoints
  } = project;

  const map = {};
  const totalValues =
    calculationKey === 'counts' ? totalCounts : totalStoryPoints;

  const {
    [countsKey]: lastIterationCounts,
    [storyPointsKey]: lastIterationStoryPoints
  } = iterations.at(-1) || {};

  const lastIterationTotals =
    calculationKey === 'counts'
      ? lastIterationCounts
      : lastIterationStoryPoints;

  const labels = fetchGraphLabels({ iterations, countsKey, storyPointsKey });

  iterations.forEach((item, index) => {
    const {
      [countsKey]: counts,
      [storyPointsKey]: storyPoints,
      start: date,
      name
    } = item;

    const iterationValues =
      calculationKey === 'counts' ? counts || {} : storyPoints || {};

    labels.forEach(label => {
      const currentValue = map[label] || {
        data: [],

        value: forceTotal
          ? Math.round(get(totalValues, label))
          : isScrumProject
          ? Math.round(get(totalValues, label))
          : Math.round(get(lastIterationTotals, label, 0)),
        category: label,
        key: label,
        iterations: [],
        values: []
      };
      currentValue.iterations.push({ date, name });
      currentValue.data.push(iterationValues[label] || 0);
      currentValue.values.push(iterationValues[label] || 0);

      map[label] = currentValue;
    });
  });
  return Object.values(map);
};

export const getTotalsFromIssuesData = issuesData => {
  const totals = issuesData.reduce((issues, issue) => {
    issues[issue.key] = Math.round(issue.value || 0);
    return issues;
  }, {});

  return totals;
};

export const getDeliveryTextsByKey = (deliveryKey, isProjectsView) => {
  const titleKey = isProjectsView ? 'projectsViewTitle' : 'title';
  const tooltipKey = isProjectsView
    ? 'projectsViewTitleTooltip'
    : 'titleTooltip';

  const itemTitle = get(
    DELIVERY_STATES_KEYS,
    `[${deliveryKey}][${titleKey}]`,
    deliveryKey
  );
  const tooltipTitle = get(
    DELIVERY_STATES_KEYS,
    `[${deliveryKey}][${tooltipKey}]`,
    deliveryKey
  );
  const issuesTableTitle = get(
    DELIVERY_STATES_KEYS,
    `[${deliveryKey}].issuesTableTitle`,
    deliveryKey
  );

  return { itemTitle, tooltipTitle, issuesTableTitle };
};

export const deliveryTypeFormatter = (status, completed) =>
  `${status}_${completed ? 'completed' : 'uncompleted'}`;
